import { useCallback, useEffect, useState } from "react";
import {
  impersonationWhoami,
  startImpersonation,
  stopImpersonation,
} from "../../lib/api/Api";
import { useUser } from "../../lib/contexts/UserContextUtils";

interface UserSummary {
  id: string;
  username: string | null;
}

interface WhoAmI {
  impersonating: boolean;
  isDeveloper: boolean;
  actor?: UserSummary;
  target?: UserSummary;
  expiresAt?: string;
}

/**
 * Global, always-mounted control for the developer impersonation feature.
 *
 * - While impersonating: a prominent banner naming the impersonated user and a
 *   one-click "Exit impersonation" that drops straight back to the developer
 *   account (clears the imp_token cookie server-side, then hard-reloads so every
 *   cached fetch / context resets to the real user).
 * - For a developer not impersonating: a slim launcher to start a session.
 * - For everyone else: renders nothing.
 */
export function ImpersonationBanner() {
  // Only logged-in sessions can be impersonating or be a developer, so gate the
  // whoami probe on an authenticated user — anonymous visitors (the public
  // homepage) never hit the endpoint.
  const { user, initialized } = useUser();
  const [state, setState] = useState<WhoAmI | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await impersonationWhoami();
      if (!res.ok) {
        setState(null);
        return;
      }
      const body = (await res.json()) as { data: WhoAmI };
      setState(body.data);
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => {
    if (!initialized) return;
    if (!user) {
      setState(null);
      return;
    }
    void refresh();
  }, [refresh, user, initialized]);

  const handleExit = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await stopImpersonation();
    } catch {
      /* exiting always clears the cookie server-side; reload regardless */
    }
    // Hard reload: resets UserContext, fetch cache, and every guild/quota view
    // back to the real developer in one clean paint.
    window.location.reload();
  }, []);

  if (!state) return null;

  if (state.impersonating) {
    const target = state.target?.username
      ? `${state.target.username} (${state.target?.id})`
      : state.target?.id;
    return (
      <div className="sticky top-0 z-[100] w-full bg-amber-500 text-amber-950 shadow-md">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
          <span className="font-semibold">⚠ Impersonating</span>
          <span className="notranslate truncate font-medium">{target}</span>
          <span className="hidden opacity-80 sm:inline">
            · read-only · acting as{" "}
            <span className="notranslate">
              {state.actor?.username ?? state.actor?.id}
            </span>
          </span>
          <button
            onClick={handleExit}
            disabled={busy}
            className="ml-auto cursor-pointer rounded-md bg-amber-950 px-3 py-1 font-semibold text-amber-50 transition hover:bg-amber-900 disabled:opacity-60"
          >
            {busy ? "Exiting…" : "Exit impersonation"}
          </button>
        </div>
      </div>
    );
  }

  if (state.isDeveloper) {
    return <DeveloperLauncher onStarted={() => window.location.reload()} error={error} setError={setError} />;
  }

  return null;
}

function DeveloperLauncher({
  onStarted,
  error,
  setError,
}: {
  onStarted: () => void;
  error: string | null;
  setError: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setBusy(true);
      setError(null);
      try {
        const res = await startImpersonation(targetId.trim(), reason.trim());
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as {
            message?: string;
          } | null;
          setError(body?.message ?? `Failed (HTTP ${res.status})`);
          setBusy(false);
          return;
        }
        onStarted();
      } catch {
        setError("Network error");
        setBusy(false);
      }
    },
    [targetId, reason, onStarted, setError]
  );

  return (
    <div className="sticky top-0 z-[100] w-full border-b border-indigo-500/30 bg-indigo-950/95 text-indigo-100 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-1.5 text-xs">
        <span className="font-semibold text-indigo-300">DEV</span>
        {!open ? (
          <button
            onClick={() => setOpen(true)}
            className="cursor-pointer rounded px-2 py-0.5 font-medium text-indigo-200 hover:bg-white/10"
          >
            Impersonate a user…
          </button>
        ) : (
          // ID-free action: without one, form.action is the page URL (Discord
          // server ID), which GA's form_start reports as form_destination;
          // submit is always handled in JS
          <form onSubmit={submit} action="/dashboard" className="flex flex-1 flex-wrap items-center gap-2">
            <input
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              placeholder="Target user ID"
              inputMode="numeric"
              pattern="\d+"
              required
              className="notranslate w-40 rounded border border-white/15 bg-black/30 px-2 py-1 text-indigo-50 placeholder:text-indigo-300/50 focus:border-indigo-400 focus:outline-none"
            />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (audited)"
              required
              minLength={3}
              maxLength={500}
              className="min-w-[12rem] flex-1 rounded border border-white/15 bg-black/30 px-2 py-1 text-indigo-50 placeholder:text-indigo-300/50 focus:border-indigo-400 focus:outline-none"
            />
            <button
              type="submit"
              disabled={busy}
              className="cursor-pointer rounded bg-indigo-500 px-3 py-1 font-semibold text-white transition hover:bg-indigo-400 disabled:opacity-60"
            >
              {busy ? "Starting…" : "Start"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              className="cursor-pointer rounded px-2 py-1 text-indigo-300 hover:bg-white/10"
            >
              Cancel
            </button>
            {error && <span className="text-red-300">{error}</span>}
          </form>
        )}
      </div>
    </div>
  );
}
